import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { companiesAPI } from '../services/api';

const CompaniesDebug = () => {
  const { employee, hasPermission } = useAuth();
  const [apiTest, setApiTest] = useState(null);
  const [loading, setLoading] = useState(false);

  const testCompaniesAPI = async () => {
    setLoading(true);
    try {
      console.log('🧪 Testing Companies API...');
      const response = await companiesAPI.getAll({ is_active: true });
      console.log('✅ API Response:', response);
      setApiTest(response.data);
    } catch (error) {
      console.error('❌ API Error:', error);
      setApiTest({ error: error.message, details: error.response?.data });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    testCompaniesAPI();
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
        🔍 Companies Module Debug Panel
      </h2>
      
      {/* User Information */}
      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <h3 className="text-lg font-semibold mb-3 text-blue-900 dark:text-blue-100">
          👤 User Information
        </h3>
        <div className="space-y-2 text-sm">
          <p><strong>Name:</strong> {employee?.name || 'N/A'}</p>
          <p><strong>Email:</strong> {employee?.email || 'N/A'}</p>
          <p><strong>Role:</strong> {employee?.role || 'N/A'}</p>
          <p><strong>Is Active:</strong> {employee?.is_active ? '✅ Yes' : '❌ No'}</p>
          <p><strong>Is Verified:</strong> {employee?.is_verified ? '✅ Yes' : '❌ No'}</p>
        </div>
      </div>

      {/* Permissions Check */}
      <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
        <h3 className="text-lg font-semibold mb-3 text-green-900 dark:text-green-100">
          🔐 Permissions Check
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center space-x-2">
            <span>companies_view:</span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              hasPermission('companies_view') 
                ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' 
                : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
            }`}>
              {hasPermission('companies_view') ? '✅ GRANTED' : '❌ DENIED'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span>companies_create:</span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              hasPermission('companies_create') 
                ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' 
                : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
            }`}>
              {hasPermission('companies_create') ? '✅ GRANTED' : '❌ DENIED'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span>companies_update:</span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              hasPermission('companies_update') 
                ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' 
                : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
            }`}>
              {hasPermission('companies_update') ? '✅ GRANTED' : '❌ DENIED'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span>companies_delete:</span>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              hasPermission('companies_delete') 
                ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' 
                : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
            }`}>
              {hasPermission('companies_delete') ? '✅ GRANTED' : '❌ DENIED'}
            </span>
          </div>
        </div>
        
        <div className="mt-4">
          <p className="text-sm"><strong>All Permissions:</strong></p>
          <div className="mt-2 max-h-32 overflow-y-auto bg-gray-100 dark:bg-gray-700 p-2 rounded text-xs">
            {employee?.permissions && employee.permissions.length > 0 ? (
              employee.permissions.map((perm, index) => (
                <div key={index} className="py-1">{perm}</div>
              ))
            ) : (
              <p className="text-red-600 dark:text-red-400">No permissions found</p>
            )}
          </div>
        </div>
      </div>

      {/* API Test Results */}
      <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
        <h3 className="text-lg font-semibold mb-3 text-yellow-900 dark:text-yellow-100">
          🧪 API Test Results
        </h3>
        <button
          onClick={testCompaniesAPI}
          disabled={loading}
          className="mb-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors"
        >
          {loading ? '🔄 Testing...' : '🧪 Test API Again'}
        </button>
        
        <div className="mt-4 max-h-64 overflow-y-auto bg-gray-100 dark:bg-gray-700 p-4 rounded text-xs">
          <pre>{JSON.stringify(apiTest, null, 2)}</pre>
        </div>
      </div>

      {/* Navigation Test */}
      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
        <h3 className="text-lg font-semibold mb-3 text-purple-900 dark:text-purple-100">
          🧭 Navigation Test
        </h3>
        <div className="space-y-2 text-sm">
          <p><strong>Should Companies show in sidebar?</strong></p>
          <p className={`font-medium ${
            hasPermission('companies_view') 
              ? 'text-green-600 dark:text-green-400' 
              : 'text-red-600 dark:text-red-400'
          }`}>
            {hasPermission('companies_view') 
              ? '✅ YES - Companies should appear in sidebar' 
              : '❌ NO - Missing companies_view permission'}
          </p>
          
          <div className="mt-4">
            <a 
              href="/companies" 
              className="inline-block px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              🔗 Direct Link to Companies Page
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompaniesDebug;